import { useMemo, useState } from 'react'
import { UserRole } from '@kfolks/types'
import { roleAtLeast } from '@/auth'
import { useCreateClient, useUpdateClient, useDeleteClient } from '../hooks/useClients'
import { useAuth } from '@/hooks/useAuth'
import { useFilteredQuery } from '@/shared/hooks/useFilteredQuery'
import { FilterBar } from '@/shared/components/molecules/FilterBar'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/components/atoms/ui/table'
import { Button } from '@/shared/components/atoms/ui/button'
import { Input } from '@/shared/components/atoms/ui/input'
import { Badge } from '@/shared/components/atoms/ui/badge'

interface ClientRow {
	clientId: string
	name: string
	code: string
	parentClientId: string | null
	productiveCompanyId: string | null
	legalEntity: string | null
	countryRegion: string | null
	isActive: boolean
	createdAt: string
}

interface EditableFields {
	name: string
	code: string
	parentClientId: string
	productiveCompanyId: string
	legalEntity: string
	countryRegion: string
}

const EMPTY_EDIT: EditableFields = {
	name: '',
	code: '',
	parentClientId: '',
	productiveCompanyId: '',
	legalEntity: '',
	countryRegion: '',
}

export function ClientsPage() {
	const { user } = useAuth()
	const isSuperAdmin = roleAtLeast(user?.role, UserRole.SUPER_ADMIN)
	const {
		data: clients,
		isLoading,
		error,
		tempFilters,
		setTempFilters,
		applyFilters,
		clearFilters,
	} = useFilteredQuery<ClientRow>({
		endpoint: '/clients',
		queryKey: 'clients',
	})
	const createMutation = useCreateClient()
	const updateMutation = useUpdateClient()
	const deleteMutation = useDeleteClient()

	const [editingId, setEditingId] = useState<string | null>(null)
	const [editingData, setEditingData] = useState<EditableFields | null>(null)
	const [isAdding, setIsAdding] = useState(false)
	const [newRow, setNewRow] = useState<EditableFields>(EMPTY_EDIT)

	const { parents, clientMap, sorted } = useMemo(() => {
		const cm = new Map(clients.map((c) => [c.clientId, c]))
		const ps = clients.filter((c) => c.parentClientId === null).sort((a, b) => a.name.localeCompare(b.name))
		const orphans = clients.filter((c) => c.parentClientId !== null && !cm.has(c.parentClientId))
		const grouped: ClientRow[] = []
		for (const parent of ps) {
			grouped.push(parent)
			const children = clients
				.filter((c) => c.parentClientId === parent.clientId)
				.sort((a, b) => a.name.localeCompare(b.name))
			grouped.push(...children)
		}
		grouped.push(...orphans.sort((a, b) => a.name.localeCompare(b.name)))
		return { parents: ps, clientMap: cm, sorted: grouped }
	}, [clients])

	const handleSaveNew = async () => {
		if (!newRow.name || !newRow.code) return
		await createMutation.mutateAsync({
			name: newRow.name,
			code: newRow.code,
			parentClientId: newRow.parentClientId || null,
			productiveCompanyId: newRow.productiveCompanyId || null,
			legalEntity: newRow.legalEntity || null,
			countryRegion: newRow.countryRegion || null,
		})
		setIsAdding(false)
		setNewRow(EMPTY_EDIT)
	}

	const handleSaveEdit = async () => {
		if (!editingData || !editingId) return
		await updateMutation.mutateAsync({
			id: editingId,
			data: {
				name: editingData.name,
				code: editingData.code,
				parentClientId: editingData.parentClientId || null,
				productiveCompanyId: editingData.productiveCompanyId || null,
				legalEntity: editingData.legalEntity || null,
				countryRegion: editingData.countryRegion || null,
			},
		})
		setEditingId(null)
		setEditingData(null)
	}

	const handleDelete = async (id: string) => {
		if (!confirm('Are you sure you want to delete this client?')) return
		await deleteMutation.mutateAsync(id)
	}

	if (isLoading) return <div className="text-center py-8">Loading clients...</div>
	if (error) return <div className="text-center py-8 text-red-600">Error: {error.message}</div>

	const parentOptions = parents.map((p) => ({ value: p.clientId, label: p.name }))

	return (
		<div>
			<div className="flex justify-between items-center mb-6">
				<h1 className="text-2xl font-bold text-foreground">Clients</h1>
				{isSuperAdmin && (
					<Button onClick={() => setIsAdding(true)} disabled={isAdding} size="sm">
						Add Client
					</Button>
				)}
			</div>

			<FilterBar
				filters={[
					{ key: 'name', label: 'Name', type: 'text' },
					{ key: 'code', label: 'Code', type: 'text' },
					{ key: 'countryRegion', label: 'Country / Region', type: 'text' },
				]}
				values={tempFilters}
				onChange={setTempFilters}
				onApply={applyFilters}
				onClear={clearFilters}
			/>

			<div className="border rounded-lg">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Name</TableHead>
							<TableHead>Code</TableHead>
							<TableHead>Parent</TableHead>
							<TableHead>Country / Region</TableHead>
							<TableHead>Legal Entity</TableHead>
							<TableHead>Productive ID</TableHead>
							<TableHead>Status</TableHead>
							<TableHead className="text-right">Actions</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{isAdding && (
							<TableRow>
								<TableCell>
									<Input
										value={newRow.name}
										onChange={(e) => setNewRow({ ...newRow, name: e.target.value })}
										placeholder="Client Name"
									/>
								</TableCell>
								<TableCell>
									<Input
										value={newRow.code}
										onChange={(e) => setNewRow({ ...newRow, code: e.target.value })}
										placeholder="client-code"
										className="font-mono"
									/>
								</TableCell>
								<TableCell>
									<select
										value={newRow.parentClientId}
										onChange={(e) => setNewRow({ ...newRow, parentClientId: e.target.value })}
										className="border rounded px-2 py-1 text-sm w-full"
									>
										<option value="">— None (is parent) —</option>
										{parentOptions.map((p) => (
											<option key={p.value} value={p.value}>
												{p.label}
											</option>
										))}
									</select>
								</TableCell>
								<TableCell>
									<Input
										value={newRow.countryRegion}
										onChange={(e) => setNewRow({ ...newRow, countryRegion: e.target.value })}
										placeholder="e.g. Mexico"
									/>
								</TableCell>
								<TableCell>
									<Input
										value={newRow.legalEntity}
										onChange={(e) => setNewRow({ ...newRow, legalEntity: e.target.value })}
										placeholder="Legal entity"
									/>
								</TableCell>
								<TableCell>
									<Input
										value={newRow.productiveCompanyId}
										onChange={(e) => setNewRow({ ...newRow, productiveCompanyId: e.target.value })}
										placeholder="Productive company id"
										className="font-mono"
									/>
								</TableCell>
								<TableCell>
									<Badge>Active</Badge>
								</TableCell>
								<TableCell className="text-right">
									<div className="flex items-center justify-end gap-1">
										<Button
											onClick={() => void handleSaveNew()}
											size="sm"
											disabled={createMutation.isPending}
											className="h-7 px-2 text-xs"
										>
											Save
										</Button>
										<Button onClick={() => setIsAdding(false)} size="sm" variant="outline" className="h-7 px-2 text-xs">
											Cancel
										</Button>
									</div>
								</TableCell>
							</TableRow>
						)}
						{sorted.map((client) => {
							const isEditing = editingId === client.clientId
							const isChild = client.parentClientId !== null
							const parent = client.parentClientId ? clientMap.get(client.parentClientId) : null
							return (
								<TableRow key={client.clientId}>
									<TableCell className={isChild ? 'pl-8 text-foreground' : 'font-medium'}>
										{isEditing ? (
											<Input
												value={editingData?.name ?? ''}
												onChange={(e) => setEditingData(editingData ? { ...editingData, name: e.target.value } : null)}
											/>
										) : (
											<span>
												{isChild && <span className="text-muted-foreground mr-1">↳</span>}
												{client.name}
											</span>
										)}
									</TableCell>
									<TableCell className="font-mono text-sm">
										{isEditing ? (
											<Input
												value={editingData?.code ?? ''}
												onChange={(e) => setEditingData(editingData ? { ...editingData, code: e.target.value } : null)}
												className="font-mono"
											/>
										) : (
											client.code
										)}
									</TableCell>
									<TableCell className="text-sm">
										{isEditing ? (
											<select
												value={editingData?.parentClientId ?? ''}
												onChange={(e) =>
													setEditingData(editingData ? { ...editingData, parentClientId: e.target.value } : null)
												}
												className="border rounded px-2 py-1 text-sm w-full"
											>
												<option value="">— None —</option>
												{parentOptions
													.filter((p) => p.value !== client.clientId)
													.map((p) => (
														<option key={p.value} value={p.value}>
															{p.label}
														</option>
													))}
											</select>
										) : parent ? (
											parent.name
										) : (
											<span className="text-muted-foreground">—</span>
										)}
									</TableCell>
									<TableCell className="text-sm">
										{isEditing ? (
											<Input
												value={editingData?.countryRegion ?? ''}
												onChange={(e) =>
													setEditingData(editingData ? { ...editingData, countryRegion: e.target.value } : null)
												}
											/>
										) : (
											client.countryRegion || <span className="text-muted-foreground">—</span>
										)}
									</TableCell>
									<TableCell className="text-sm">
										{isEditing ? (
											<Input
												value={editingData?.legalEntity ?? ''}
												onChange={(e) =>
													setEditingData(editingData ? { ...editingData, legalEntity: e.target.value } : null)
												}
											/>
										) : (
											client.legalEntity || <span className="text-muted-foreground">—</span>
										)}
									</TableCell>
									<TableCell className="font-mono text-xs">
										{isEditing ? (
											<Input
												value={editingData?.productiveCompanyId ?? ''}
												onChange={(e) =>
													setEditingData(editingData ? { ...editingData, productiveCompanyId: e.target.value } : null)
												}
												className="font-mono"
											/>
										) : (
											client.productiveCompanyId || <span className="text-muted-foreground">—</span>
										)}
									</TableCell>
									<TableCell>
										{client.isActive ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive</Badge>}
									</TableCell>
									<TableCell className="text-right">
										{isSuperAdmin &&
											(isEditing ? (
												<div className="flex items-center justify-end gap-1">
													<Button
														onClick={() => void handleSaveEdit()}
														size="sm"
														disabled={updateMutation.isPending}
														className="h-7 px-2 text-xs"
													>
														Save
													</Button>
													<Button
														onClick={() => {
															setEditingId(null)
															setEditingData(null)
														}}
														size="sm"
														variant="outline"
														className="h-7 px-2 text-xs"
													>
														Cancel
													</Button>
												</div>
											) : (
												<div className="flex items-center justify-end gap-1">
													<Button
														onClick={() => {
															setEditingId(client.clientId)
															setEditingData({
																name: client.name,
																code: client.code,
																parentClientId: client.parentClientId ?? '',
																productiveCompanyId: client.productiveCompanyId ?? '',
																legalEntity: client.legalEntity ?? '',
																countryRegion: client.countryRegion ?? '',
															})
														}}
														size="sm"
														variant="outline"
														className="h-7 px-2 text-xs"
													>
														Edit
													</Button>
													<Button
														onClick={() => void handleDelete(client.clientId)}
														size="sm"
														variant="destructive"
														disabled={deleteMutation.isPending}
														className="h-7 px-2 text-xs"
													>
														Delete
													</Button>
												</div>
											))}
									</TableCell>
								</TableRow>
							)
						})}
						{!isAdding && clients.length === 0 && (
							<TableRow>
								<TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
									No clients found.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
		</div>
	)
}
